import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Building2, User, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NormalizedAppointment } from '../../hooks/useStatistics';

interface Props {
  appointments: NormalizedAppointment[];
  loading: boolean;
}

const statusConfig: Record<string, { label: string; classes: string }> = {
  effectue: { label: 'Effectue', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  confirme: { label: 'Confirme', classes: 'bg-blue-50 text-blue-700 border-blue-200' },
  planifie: { label: 'Planifie', classes: 'bg-gray-100 text-gray-600 border-gray-200' },
  reporte: { label: 'Reporte', classes: 'bg-amber-50 text-amber-700 border-amber-200' },
  annule: { label: 'Annule', classes: 'bg-red-50 text-red-700 border-red-200' },
  no_reply: { label: 'No reply', classes: 'bg-gray-100 text-gray-500 border-gray-200' },
  autre: { label: 'Autre', classes: 'bg-gray-100 text-gray-500 border-gray-200' },
};

function formatDate(dateStr: string, timeStr: string | null): string {
  const d = new Date(dateStr);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const base = `${day}/${month}/${year}`;
  if (timeStr) {
    const parts = timeStr.split(':');
    return `${base} a ${parts[0]}h${parts[1]}`;
  }
  return base;
}

const StatisticsRecentAppointments: React.FC<Props> = ({ appointments, loading }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.7 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <div className="h-5 bg-gray-200 rounded w-56 mb-6 animate-pulse" />
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-14 bg-gray-50 rounded-lg animate-pulse" />
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.7 }}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-900">Derniers RDV obtenus</h3>
        <button
          onClick={() => navigate('/appointments')}
          className="text-sm font-medium text-rose-500 hover:text-rose-600 inline-flex items-center space-x-1 transition-colors"
        >
          <span>Voir tous les rendez-vous</span>
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>

      {appointments.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">
          Aucun rendez-vous sur cette periode
        </div>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 pr-4">Date</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 pr-4">Prospect</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 pr-4">Entreprise</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3 pr-4">Poste</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-3">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {appointments.map((appt, index) => {
                  const config = statusConfig[appt.status_normalized] || statusConfig.autre;
                  return (
                    <motion.tr
                      key={appt.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.8 + index * 0.03 }}
                      className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/appointments/${appt.id}`)}
                    >
                      <td className="py-3.5 pr-4">
                        <span className="text-sm text-gray-900 font-medium">
                          {formatDate(appt.date, appt.time)}
                        </span>
                      </td>
                      <td className="py-3.5 pr-4">
                        <span className="text-sm text-gray-700">{appt.prospect_name || '-'}</span>
                      </td>
                      <td className="py-3.5 pr-4">
                        <span className="text-sm text-gray-700">{appt.company_name || '-'}</span>
                      </td>
                      <td className="py-3.5 pr-4">
                        <span className="text-sm text-gray-500">{appt.job_title || '-'}</span>
                      </td>
                      <td className="py-3.5">
                        <span className={`inline-flex text-xs font-medium px-2.5 py-1 rounded-full border ${config.classes}`}>
                          {config.label}
                        </span>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {appointments.map((appt, index) => {
              const config = statusConfig[appt.status_normalized] || statusConfig.autre;
              return (
                <motion.div
                  key={appt.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 + index * 0.03 }}
                  className="bg-gray-50 rounded-lg p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => navigate(`/appointments/${appt.id}`)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2 text-sm text-gray-900 font-medium">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      <span>{formatDate(appt.date, appt.time)}</span>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${config.classes}`}>
                      {config.label}
                    </span>
                  </div>
                  {appt.prospect_name && (
                    <div className="flex items-center space-x-2 text-sm text-gray-700 mb-1">
                      <User className="h-3.5 w-3.5 text-gray-400" />
                      <span>{appt.prospect_name}</span>
                    </div>
                  )}
                  {appt.company_name && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Building2 className="h-3.5 w-3.5 text-gray-400" />
                      <span>{appt.company_name}</span>
                    </div>
                  )}
                  {appt.job_title && (
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                      <Briefcase className="h-3.5 w-3.5 text-gray-400" />
                      <span>{appt.job_title}</span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </>
      )}
    </motion.div>
  );
};

export default StatisticsRecentAppointments;
